import {
  RunningUpload,
  RunningUploadsRecord,
  UploadSequence,
} from 'types/RunningUpload';
import { RunningUploadStatus } from 'constants/runningUpload';
import { v4 } from 'uuid';
import { MediaApi, AssetProcessingFailedError } from 'api/media';
import { AssetStatus } from 'types/AssetStatus';
import { Asset } from 'types/Attachments';
import { MediaLibraryItem } from 'types/MediaLibrary';
import {
  documentToMediaLibraryItem,
  imageAssetToMediaLibraryItem,
  imageToMediaLibraryItem,
  unprocessedVideoToMediaLibraryItem,
  videoAssetToMediaLibraryItem,
} from 'utils/mediaLibrary';
import Logger from 'utils/logger';
import axios from 'axios';
import { ApiClientError } from 'api/client';
import i18n from 'app/utils/i18n';
import {
  CreateRunningUploadsReturn,
  UploadDestination,
} from 'app/utils/runningUploads';

async function createRunningUpload(
  id: string,
  type: 'image' | 'video' | 'document',
  file: File,
): Promise<RunningUpload> {
  function getPreview() {
    if (type === 'video' || type === 'document') {
      return undefined;
    }

    return URL.createObjectURL(file);
  }

  return {
    id,
    base64Preview: await getPreview(),
    file,
    type,
    status: RunningUploadStatus.Pending,
    progress: undefined,
    cancelHandler: axios.CancelToken.source(),
  };
}

type CreateRunningUploadJobsParams = {
  files: { videos: File[]; images: File[]; documents: File[] };
  pageId: number;
  destination: UploadDestination;
  userAccountPublicId: string;
  userId: number;
  uploadSequence: UploadSequence;
};

async function uploadToLegacyLibrary(
  runningUpload: RunningUpload,
  {
    albumId,
    pageId,
    userAccountPublicId,
    userId,
  }: {
    albumId?: number;
    pageId: number;
    userAccountPublicId: string;
    userId: number;
  },
) {
  if (runningUpload.type === 'image') {
    const response = await MediaApi.uploadImage({
      file: runningUpload.file,
      albumId,
      cancelToken: runningUpload.cancelHandler.token,
      onProgress: (progress: number) => (runningUpload.progress = progress),
    });

    const uploadedImage = Object.values(response)[0];

    runningUpload.result = imageToMediaLibraryItem(uploadedImage);
  }

  if (runningUpload.type === 'video') {
    runningUpload.progress = 0;

    const res = await MediaApi.uploadVideo({
      file: runningUpload.file,
      cancelToken: runningUpload.cancelHandler.token,
      albumId,
      pageId,
      userAccountPublicId,
      userId,
      onProgress: (progress: number) => (runningUpload.progress = progress),
    });

    runningUpload.result = unprocessedVideoToMediaLibraryItem(res.videoId);
  }

  if (runningUpload.type === 'document') {
    runningUpload.progress = 0;

    const asset = await MediaApi.simpleUploadAsset({
      file: runningUpload.file,
      cancelToken: runningUpload.cancelHandler.token,
      onProgress: (progress: number) => (runningUpload.progress = progress),
    });

    runningUpload.result = documentToMediaLibraryItem(asset);
  }

  runningUpload.status = RunningUploadStatus.Done;
}

export async function createRunningUploadsFromFiles({
  files,
  destination,
  pageId,
  userAccountPublicId,
  userId,
  uploadSequence,
}: CreateRunningUploadJobsParams): Promise<CreateRunningUploadsReturn> {
  const runningUploads: RunningUploadsRecord = {};

  for (const image of files.images) {
    const id = v4();
    runningUploads[id] = await createRunningUpload(id, 'image', image);
  }

  for (const video of files.videos) {
    const id = v4();
    runningUploads[id] = await createRunningUpload(id, 'video', video);
  }

  for (const document of files.documents) {
    const id = v4();
    runningUploads[id] = await createRunningUpload(id, 'document', document);
  }

  const jobs = Object.values(runningUploads).map(
    (runningUpload) => async () => {
      runningUpload.status = RunningUploadStatus.Uploading;

      try {
        if (destination.library === 'legacy') {
          await uploadToLegacyLibrary(runningUpload, {
            albumId: destination.albumId,
            pageId,
            userAccountPublicId,
            userId,
          });
          return runningUpload;
        }

        const { albumId } = destination;
        const startProcessingPoll = (
          uploaded: Asset,
          buildResult: (ready: Asset) => MediaLibraryItem,
        ) => {
          runningUpload.status = RunningUploadStatus.Processing;
          runningUpload.processingPromise = (async () => {
            try {
              const ready = await MediaApi.pollAssetUntilReady(uploaded.id, {
                cancelToken: runningUpload.cancelHandler.token,
              });
              runningUpload.result = buildResult(ready);
              runningUpload.status = RunningUploadStatus.Done;
            } catch (e) {
              runningUpload.status = RunningUploadStatus.Failed;
              if (axios.isCancel(e)) {
                return;
              }
              Logger.error(e);
              runningUpload.error =
                (e instanceof ApiClientError && e?.userMessage) ||
                (e instanceof AssetProcessingFailedError
                  ? i18n.t('assetProcessingFailed')
                  : 'Unable to upload. Please see the requirements and recommended sizes of files below.');
            }
          })();
        };

        if (runningUpload.type === 'image') {
          const uploaded = await MediaApi.uploadAsset({
            file: runningUpload.file,
            albumId,
            pageId,
            cancelToken: runningUpload.cancelHandler.token,
            onProgress: (progress: number) =>
              (runningUpload.progress = progress),
          });

          if (uploaded.status === AssetStatus.READY) {
            runningUpload.result = imageAssetToMediaLibraryItem(uploaded);
          } else {
            startProcessingPoll(uploaded, imageAssetToMediaLibraryItem);
          }
        }

        if (runningUpload.type === 'video') {
          runningUpload.progress = 0;

          const uploaded = await MediaApi.uploadAsset({
            file: runningUpload.file,
            albumId,
            pageId,
            cancelToken: runningUpload.cancelHandler.token,
            onProgress: (progress: number) =>
              (runningUpload.progress = progress),
          });

          if (uploaded.status === AssetStatus.READY) {
            runningUpload.result = videoAssetToMediaLibraryItem(uploaded);
          } else {
            startProcessingPoll(uploaded, videoAssetToMediaLibraryItem);
          }
        }

        if (runningUpload.type === 'document') {
          runningUpload.progress = 0;

          const uploaded = await MediaApi.uploadAsset({
            file: runningUpload.file,
            albumId,
            pageId,
            cancelToken: runningUpload.cancelHandler.token,
            onProgress: (progress: number) =>
              (runningUpload.progress = progress),
          });

          if (uploaded.status === AssetStatus.READY) {
            runningUpload.result = documentToMediaLibraryItem(uploaded);
          } else {
            startProcessingPoll(uploaded, documentToMediaLibraryItem);
          }
        }

        const currentStatus = runningUpload.status as RunningUploadStatus;
        if (
          currentStatus !== RunningUploadStatus.Processing &&
          currentStatus !== RunningUploadStatus.Failed
        ) {
          runningUpload.status = RunningUploadStatus.Done;
        }
      } catch (e) {
        runningUpload.status = RunningUploadStatus.Failed;
        if (axios.isCancel(e)) {
          return runningUpload;
        }
        Logger.error(e);
        runningUpload.error =
          (e instanceof ApiClientError && e?.userMessage) ||
          'Unable to upload. Please see the requirements and recommended sizes of files below.';
      }

      return runningUpload;
    },
  );

  return {
    jobs: uploadSequence === 'lifo' ? jobs.reverse() : jobs,
    runningUploads,
  };
}
